import { useQuery } from '@apollo/client'
import {
    Autocomplete,
    Checkbox,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    type AutocompleteProps,
    type SelectChangeEvent,
} from '@mui/material'
import {
    CandidateFilterCategory,
    SearchIndexField,
    type GetRosterStartDateOptionsQuery,
} from '__generated__/graphql'
import { useMemo, useState } from 'react'
import { useRosterSearchFilter } from 'recruiter/hooks'
import { GetRosterStartDateOptions } from 'shared/graphql/startDates.gql'

interface RosterStartDateFilterProps {
    compact?: boolean
}

export function RosterStartDateFilter({
    compact = false,
}: RosterStartDateFilterProps) {
    const { value, setValue } = useRosterSearchFilter(
        SearchIndexField.RosterStartDate
    )
    const [startDateOptions, setStartDateOptions] = useState<
        GetRosterStartDateOptionsQuery['rosterStartDates']
    >([])

    const { loading: areStartDateOptionsLoading } = useQuery(
        GetRosterStartDateOptions,
        {
            onCompleted(data) {
                setStartDateOptions(data?.rosterStartDates ?? [])
            },
        }
    )

    const startDatesInFilter = useMemo(() => {
        const startDateIdsInFilter = value?.value?.stringArray ?? []
        return startDateOptions.filter((startDateOption) =>
            startDateIdsInFilter.includes(startDateOption)
        )
    }, [startDateOptions, value])

    const handleChange = (newValues: string[]) => {
        if (newValues.length === 0) {
            setValue(null)
            return
        }

        setValue({
            category: CandidateFilterCategory.All,
            value: { stringArray: newValues },
        })
    }

    const handleAutocompleteChange: AutocompleteProps<
        (typeof startDateOptions)[number],
        true,
        false,
        false
    >['onChange'] = (_, newValues) => {
        handleChange(newValues)
    }

    const handleSelectChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value
        handleChange(typeof value === 'string' ? [] : value)
    }

    const [open, setOpen] = useState(false)

    if (compact) {
        return (
            <FormControl size="small" fullWidth>
                <InputLabel>Start Date</InputLabel>
                <Select
                    multiple
                    open={open}
                    onOpen={() => setOpen(true)}
                    value={startDatesInFilter}
                    onChange={handleSelectChange}
                    label="Start Date"
                    renderValue={(selected) =>
                        selected.length > 0 ? `${selected.length} selected` : ''
                    }
                    MenuProps={{
                        onClose: (_, reason) => {
                            if (
                                reason === 'backdropClick' ||
                                reason === 'escapeKeyDown'
                            ) {
                                setOpen(false)
                            }
                        },
                    }}
                >
                    {startDateOptions.map((date) => (
                        <MenuItem key={date} value={date}>
                            <Checkbox
                                checked={startDatesInFilter.includes(date)}
                                size="small"
                            />
                            <ListItemText primary={date} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        )
    }

    return (
        <Autocomplete
            ChipProps={{ size: 'small' }}
            loading={areStartDateOptionsLoading}
            multiple
            onChange={handleAutocompleteChange}
            options={startDateOptions}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Start Date"
                    placeholder="Search start dates..."
                />
            )}
            value={startDatesInFilter}
        />
    )
}
